import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Grid, Paper, Tooltip, Alert } from "@mui/material";
import { Edit as EditIcon, Error as ErrorIcon } from "@mui/icons-material";

import {
  changeBillableDate,
  setShowEditDiagnosisModal,
  changeOriginalBillableDate,
  toggleNonBillable,
} from "app/store/transmission/slice/transmissionSlice";

import ReportBillingForm from "./ReportBillingForm";
import BillingDiagnosisCodeModal from "../../modals/billing_diagnosis_code/BillingDiagnosisCodeModal";

import formatDate from "utils/formatDate";
import { formatDateTime } from "utils/date";
import {
  getMultipleBillingCodes,
  getUniqueCodesWithSeparator,
} from "utils/billingCode.util";
import GridListItem from "components/common/grid/GridListItem";
import NonBillableToggle from "components/FormFields/NonBillableToggle";

const ReportBilling = ({ data }) => {
  const dispatch = useDispatch();
  const {
    reportDetail,
    showEditDiagnosisModal,
    hasReportNoteEditAccess,
    invalidBillingDateResponse,
    isNonBillable,
  } = useSelector((state) => state.transmission);

  useEffect(() => {
    if (data) {
      const billingDate = data?.report_billing_date
        ? formatDateTime(data?.report_billing_date)
        : null;
      dispatch(changeBillableDate(billingDate));
      dispatch(changeOriginalBillableDate(billingDate));
    }
  }, [data, dispatch]);

  return (
    <div className="billable_wrapper">
      <Paper variant="outlined">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Grid container rowSpacing={2} style={{ padding: "0 16px" }}>
            <Grid item xs={12}>
              <h3>Report Transmission Information</h3>
            </Grid>

            {reportDetail.explicit_non_billable && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: "12px" }}>
                  This report was marked as non-billable and cannot be reversed.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Grid container spacing={0}>
                <GridListItem
                  title="BILLING CODE"
                  subTitle={getMultipleBillingCodes(
                    reportDetail?.billingDiagnosisCodes
                  )}
                  size={hasReportNoteEditAccess ? 6 : 4}
                />

                <Grid
                  container
                  zeroMinWidth
                  xs={hasReportNoteEditAccess ? 6 : 4}
                >
                  <GridListItem
                    title="DIAGNOSIS CODE"
                    subTitle={
                      getUniqueCodesWithSeparator(
                        reportDetail?.billingDiagnosisCodes,
                        "diagnosisCode"
                      ) || "-"
                    }
                    size={hasReportNoteEditAccess ? 8 : 12}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  />

                  {hasReportNoteEditAccess &&
                    !reportDetail.explicit_non_billable && (
                      <Grid
                        item
                        xs={1}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Update Diagnosis Codes">
                          <EditIcon
                            className="action-buttons__edit ml-2"
                            onClick={() =>
                              dispatch(setShowEditDiagnosisModal(true))
                            }
                            style={{ cursor: "pointer" }}
                            data-action="edit-diagnosis-code-icon"
                          />
                        </Tooltip>
                      </Grid>
                    )}

                  {showEditDiagnosisModal && <BillingDiagnosisCodeModal />}
                </Grid>

                {invalidBillingDateResponse && (
                  <Alert
                    severity="warning"
                    icon={<ErrorIcon />}
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#FF9800",
                    }}
                  >
                    Billing date doesn’t match the{" "}
                    {invalidBillingDateResponse?.billingCycle}-day cycle. Please
                    review and try again.
                  </Alert>
                )}
                <>
                  {hasReportNoteEditAccess &&
                  !reportDetail.explicit_non_billable ? (
                    <>
                      <NonBillableToggle
                        account={reportDetail?.account}
                        nonBillable={isNonBillable}
                        onChange={(event) => {
                          dispatch(toggleNonBillable(event.target.checked));
                          dispatch(changeBillableDate(null));
                        }}
                        tooltipTitle="Enable to update as a non-billable report."
                      />
                      {!isNonBillable && <ReportBillingForm data={data} />}
                    </>
                  ) : (
                    <GridListItem
                      size={hasReportNoteEditAccess ? 6 : 4}
                      title="BILLING DATE"
                      subTitle={
                        !reportDetail.explicit_non_billable &&
                        reportDetail?.last_billing_date
                          ? formatDate(reportDetail?.last_billing_date)
                          : "-"
                      }
                    />
                  )}
                </>

                {reportDetail?.last_signature_date && (
                  <GridListItem
                    size={12}
                    title="LAST SIGNATURE"
                    subTitle={
                      reportDetail.last_signature_by
                        ? `${reportDetail.last_signature_by} on ${formatDate(reportDetail.last_signature_date)}`
                        : formatDate(reportDetail.last_signature_date)
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Paper>
    </div>
  );
};

export default ReportBilling;
